import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { DOMAINS, ROLES, ROLE_STYLE, DEFAULT_PERMS_BY_ROLE, countGranted, formatRole } from '../permissionConfig';
import { NOTIFICATION_DOMAINS, FREQUENCIES, DAYS_OF_WEEK } from '../notificationFeatures';
import { API_BASE } from '../config';
import { exportSheet, todayStamp } from '../excel';

// notifRecipients/newRecipientEmail/notifSchedules are all keyed by this
// composite string — a feature is only unique within its domain, and a
// domain with no named features yet uses `feature: null` (see
// notificationFeatures.js), which still needs a stable, distinct map key.
function featureMapKey(domainKey, featureKey) { return `${domainKey}::${featureKey ?? ''}`; }

/* ---------------------------------------------------------------------- */
/* Icons                                                                   */
/* ---------------------------------------------------------------------- */
const Icon = ({ children, size = 18, ...rest }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...rest}>
        {children}
    </svg>
);
const IconUsers  = (p) => <Icon {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>;
const IconShield = (p) => <Icon {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Icon>;
const IconHome   = (p) => <Icon {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></Icon>;
const IconUser   = (p) => <Icon {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Icon>;
const IconPlus   = (p) => <Icon {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>;
const IconSearch = (p) => <Icon {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Icon>;
const IconCheck  = (p) => <Icon {...p}><polyline points="20 6 9 17 4 12" /></Icon>;
const IconX      = (p) => <Icon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Icon>;
const IconMail   = (p) => <Icon {...p}><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></Icon>;
const IconActivity = (p) => <Icon {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></Icon>;
const IconMessage = (p) => <Icon {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></Icon>;

const FILTERS = ['All roles', ...ROLES];

function initialOf(name) { return (name || '?').trim()[0]?.toUpperCase() || '?'; }

// Builds YYYY-MM-DD from LOCAL date fields, never toISOString() (which
// converts to UTC first — on a browser in IST that silently shifts the date
// back by one). Same convention as SupplyChainDashboard.jsx's isoDate/daysAgo.
function isoDate(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
const USAGE_TODAY = isoDate(new Date());
function usageDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
const USAGE_RANGES = [
    { key: 'd7',  label: 'Last 7 days',  from: usageDaysAgo(6),  to: USAGE_TODAY },
    { key: 'd30', label: 'Last 30 days', from: usageDaysAgo(29), to: USAGE_TODAY },
    { key: 'd90', label: 'Last 90 days', from: usageDaysAgo(89), to: USAGE_TODAY },
];

// All known dashboard labels (from the single source of truth), used to
// call out any dashboard with zero views in the selected period.
const ALL_DASHBOARD_LABELS = DOMAINS.flatMap(d => d.functions.map(f => f.label));

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Backend returns the DB's stored IST-wall-clock value, which the JSON
// serializer stamps with a 'Z' suffix (an artifact of how the mssql driver
// round-trips a plain DATETIME2) — so the digits are IST but the Date
// object's LOCAL getters would apply the browser's own timezone offset on
// top of them, double-shifting. Reading via the UTC getters instead gets
// back the original IST digits untouched, matching istTime.js's own
// "UTC-getter Date whose fields already read as IST wall-clock" convention.
function fmtLastActive(value) {
    if (!value) return 'Never';
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Never';
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = MONTH_ABBR[d.getUTCMonth()];
    const year = d.getUTCFullYear();
    let h = d.getUTCHours();
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${day} ${month} ${year}, ${String(h).padStart(2, '0')}:${m} ${ampm}`;
}

// "Now" shifted the same way istNow() shifts it on the backend, so
// subtracting the (already IST-labeled-as-UTC) stored value gives the
// correct real elapsed time rather than being off by 5.5 hours.
function daysSince(value) {
    if (!value) return Infinity;
    const d = new Date(value);
    if (isNaN(d.getTime())) return Infinity;
    const nowIst = Date.now() + 5.5 * 60 * 60 * 1000;
    return Math.floor((nowIst - d.getTime()) / 86400000);
}

export default function AdminConsole({ user, onLogout }) {
    const navigate = useNavigate();
    const [section, setSection]           = useState('users');
    const [animKey, setAnimKey]           = useState(0);
    const [activeFilter, setActiveFilter] = useState('All roles');
    const [searchQuery, setSearchQuery]   = useState('');
    const [users, setUsers]               = useState([]);
    const [loading, setLoading]           = useState(true);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [toast, setToast]               = useState(null);
    const [showAdd, setShowAdd]           = useState(false);
    const [addForm, setAddForm]           = useState({ username: '', password: '', role: 'kam', display_name: '' });
    const [editId, setEditId]             = useState(null);
    const [editForm, setEditForm]         = useState({});
    const [saving, setSaving]             = useState(false);
    const [msg, setMsg]                   = useState(null);
    const [confirmSignOut, setConfirmSignOut] = useState(false);
    const [notifRecipients, setNotifRecipients] = useState([]);
    const [notifSchedules, setNotifSchedules]   = useState([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [newRecipientEmail, setNewRecipientEmail] = useState({});
    // Per-KAM features (SO KAM Review Alert): KAM picked for the next add,
    // keyed like newRecipientEmail, and the KAM list from kam_vendor.
    const [newRecipientKam, setNewRecipientKam] = useState({});
    const [kamNames, setKamNames] = useState([]);
    const [usageRange, setUsageRange] = useState('d30');
    const [teamActivity, setTeamActivity] = useState([]);
    const [dashboardPopularity, setDashboardPopularity] = useState([]);
    const [dailyActive, setDailyActive] = useState([]);
    const [roleSummary, setRoleSummary] = useState([]);
    const [usageLoading, setUsageLoading] = useState(false);
    const [waWhitelist, setWaWhitelist] = useState([]);
    const [waLoading, setWaLoading] = useState(false);
    const [waAddForm, setWaAddForm] = useState({ phone: '', name: '', nickname: '', role: '' });
    const [waEditPhone, setWaEditPhone] = useState(null);
    const [waEditForm, setWaEditForm] = useState({ name: '', nickname: '', role: '' });
    const toastTimer = useRef(null);

    function getAuthHeaders() {
        return {
            'x-admin-user': user.username,
            'x-admin-pass': localStorage.getItem('sf_pass') || '',
        };
    }

    async function loadUsers() {
        setLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/auth?action=users`, { headers: getAuthHeaders() });
            const data = await res.json();
            const list = Array.isArray(data) ? data : [];
            setUsers(list);
            if (!selectedUserId && list.length > 0) setSelectedUserId(list[0].id);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }

    useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, []);

    async function loadNotifRecipients() {
        setNotifLoading(true);
        try {
            const [recRes, schedRes] = await Promise.all([
                fetch(`${API_BASE}/notification-recipients?action=list`),
                fetch(`${API_BASE}/notification-schedules?action=list`),
            ]);
            const recData = await recRes.json();
            const schedData = await schedRes.json();
            setNotifRecipients(Array.isArray(recData.recipients) ? recData.recipients : []);
            setKamNames(Array.isArray(recData.kams) ? recData.kams : []);
            setNotifSchedules(Array.isArray(schedData.schedules) ? schedData.schedules : []);
        } catch (e) { console.error(e); }
        finally { setNotifLoading(false); }
    }

    async function addNotifRecipient(domainKey, featureKey) {
        const mapKey = featureMapKey(domainKey, featureKey);
        const email = (newRecipientEmail[mapKey] || '').trim();
        if (!email) return;
        try {
            const res = await fetch(`${API_BASE}/notification-recipients?action=add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domain_key: domainKey, feature_key: featureKey, email, added_by: user?.username || null, kam_name: newRecipientKam[mapKey] || null }),
            });
            if (res.ok) {
                setNewRecipientEmail(prev => ({ ...prev, [mapKey]: '' }));
                setNewRecipientKam(prev => ({ ...prev, [mapKey]: '' }));
                await loadNotifRecipients();
                showToast(`Added ${email}`, true);
            }
        } catch (e) { console.error(e); }
    }

    async function setRecipientKam(id, kamName) {
        try {
            const res = await fetch(`${API_BASE}/notification-recipients?action=set_kam`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, kam_name: kamName || null }),
            });
            if (res.ok) {
                setNotifRecipients(prev => prev.map(r => r.id === id ? { ...r, kam_name: kamName || null } : r));
                showToast(kamName ? `Now gets only ${kamName}'s SOs` : 'Now gets all SO alerts', true);
            }
        } catch (e) { console.error(e); }
    }

    async function removeNotifRecipient(id, email) {
        try {
            const res = await fetch(`${API_BASE}/notification-recipients?action=remove`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
            });
            if (res.ok) {
                await loadNotifRecipients();
                showToast(`Removed ${email}`, false);
            }
        } catch (e) { console.error(e); }
    }

    async function saveNotifSchedule(domainKey, featureKey, patch) {
        const existing = notifSchedules.find(s => s.domain_key === domainKey && s.feature_key === featureKey);
        const body = {
            domain_key: domainKey, feature_key: featureKey,
            frequency: existing?.frequency || 'daily',
            time_ist: existing?.time_ist || '18:30',
            day_of_week: existing?.day_of_week ?? 0,
            day_of_month: existing?.day_of_month ?? 1,
            updated_by: user?.username || null,
            ...patch,
        };
        try {
            const res = await fetch(`${API_BASE}/notification-schedules?action=set`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                await loadNotifRecipients();
                showToast('Schedule saved', true);
            }
        } catch (e) { console.error(e); }
    }

    async function loadWaWhitelist() {
        setWaLoading(true);
        try {
            const res = await fetch(`${API_BASE}/whatsapp-webhook?action=list_whitelist`, { headers: getAuthHeaders() });
            const data = await res.json();
            setWaWhitelist(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) { console.error(e); }
        finally { setWaLoading(false); }
    }

    async function saveWaNumber(payload, successMsg) {
        try {
            const res = await fetch(`${API_BASE}/whatsapp-webhook?action=set_whitelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify(payload),
            });
            if (res.ok) {
                await loadWaWhitelist();
                showToast(successMsg, true);
                return true;
            }
        } catch (e) { console.error(e); }
        return false;
    }

    async function addWaNumber() {
        const phone = waAddForm.phone.trim();
        if (!phone) return;
        const ok = await saveWaNumber({ phone, name: waAddForm.name.trim() || null, nickname: waAddForm.nickname.trim() || null, role: waAddForm.role.trim() || null }, `Added ${phone}`);
        if (ok) setWaAddForm({ phone: '', name: '', nickname: '', role: '' });
    }

    async function removeWaNumber(phone) {
        try {
            const res = await fetch(`${API_BASE}/whatsapp-webhook?action=remove_whitelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ phone }),
            });
            if (res.ok) {
                await loadWaWhitelist();
                showToast(`Removed ${phone}`, false);
            }
        } catch (e) { console.error(e); }
    }

    async function loadUsageData(range) {
        const r = USAGE_RANGES.find(x => x.key === range) || USAGE_RANGES[1];
        setUsageLoading(true);
        try {
            const qs = `from=${r.from}&to=${r.to}`;
            const [teamRes, pagesRes, dailyRes, rolesRes] = await Promise.all([
                fetch(`${API_BASE}/portal-usage?action=team_activity&${qs}`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE}/portal-usage?action=dashboard_popularity&${qs}`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE}/portal-usage?action=daily_active&${qs}`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE}/portal-usage?action=role_summary&${qs}`, { headers: getAuthHeaders() }),
            ]);
            const team = await teamRes.json();
            const pages = await pagesRes.json();
            const daily = await dailyRes.json();
            const roles = await rolesRes.json();

            const rows = (team.rows || []).slice().sort((a, b) => {
                const aLast = a.last_active ? new Date(a.last_active).getTime() : -1;
                const bLast = b.last_active ? new Date(b.last_active).getTime() : -1;
                return aLast - bLast; // least active first — never-active (-1) sorts first
            });
            setTeamActivity(rows);
            setDashboardPopularity(pages.pages || []);
            // CAST(... AS DATE) also comes back 'Z'-stamped — same UTC-getter
            // reasoning as fmtLastActive above, so the chart's day label
            // doesn't shift by a day for anyone in a timezone the picks up
            // the browser's own offset.
            setDailyActive((daily.days || []).map(d => {
                const dt = new Date(d.day);
                return { day: `${String(dt.getUTCDate()).padStart(2, '0')} ${MONTH_ABBR[dt.getUTCMonth()]}`, active_users: d.active_users };
            }));
            setRoleSummary(roles.roles || []);
        } catch (e) { console.error(e); }
        finally { setUsageLoading(false); }
    }

    function exportTeamActivity() {
        const rows = teamActivity.map((u, i) => [
            i + 1, u.display_name, formatRole(u.role), fmtLastActive(u.last_active),
            u.days_active, u.total_visits, u.avg_visit_minutes ?? '—', u.most_used_page || '—',
        ]);
        exportSheet({
            headers: ['#', 'Name', 'Role', 'Last Active (IST)', 'Days Active', 'Total Visits', 'Avg Visit (min)', 'Most-Used Dashboard'],
            rows, sheetName: 'Portal Usage',
            filename: `portal-usage-${todayStamp()}.xlsx`,
        });
    }

    const goSection = (s) => {
        setSection(s); setAnimKey(k => k + 1); setMsg(null);
        if (s === 'notifications' && notifRecipients.length === 0) loadNotifRecipients();
        if (s === 'usage' && teamActivity.length === 0) loadUsageData(usageRange);
        if (s === 'whatsapp' && waWhitelist.length === 0) loadWaWhitelist();
    };

    function showToast(text, on) {
        clearTimeout(toastTimer.current);
        setToast({ msg: text, on });
        toastTimer.current = setTimeout(() => setToast(null), 2200);
    }

    async function togglePermission(domainKey, functionKey, fLabel) {
        const selUser = users.find(u => u.id === selectedUserId);
        if (!selUser) return;

        // Optimistic update
        setUsers(prev => prev.map(u => {
            if (u.id !== selectedUserId) return u;
            const perms = { ...(u.function_permissions || {}) };
            const current = perms[domainKey] || [];
            const isOn = current.includes(functionKey);
            perms[domainKey] = isOn ? current.filter(k => k !== functionKey) : [...current, functionKey];
            showToast(`${isOn ? 'Removed' : 'Granted'} ${fLabel}`, !isOn);
            return { ...u, function_permissions: perms };
        }));

        try {
            await fetch(`${API_BASE}/auth?action=toggle_permission`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ id: selectedUserId, domain: domainKey, function_key: functionKey }),
            });
        } catch (e) {
            console.error('Toggle failed, reloading to resync', e);
            loadUsers();
        }
    }

    // Sets a function's access level explicitly: 'none' | 'view' | 'edit'.
    // Reuses togglePermission (view) plus the new edit-permission action,
    // only calling whichever toggle is actually needed to reach the target
    // state from the current one.
    async function setPermissionLevel(domainKey, functionKey, fLabel, targetLevel) {
        const selUserNow = users.find(u => u.id === selectedUserId);
        if (!selUserNow) return;
        const hasView = (selUserNow.function_permissions?.[domainKey] || []).includes(functionKey);
        const hasEdit = (selUserNow.edit_permissions?.[domainKey] || []).includes(functionKey);
        const currentLevel = hasEdit ? 'edit' : hasView ? 'view' : 'none';
        if (currentLevel === targetLevel) return;

        if (targetLevel === 'none') {
            if (hasEdit) await toggleEditPermission(domainKey, functionKey, fLabel, /*silent*/ true);
            if (hasView) await togglePermission(domainKey, functionKey, fLabel);
        } else if (targetLevel === 'view') {
            if (hasEdit) await toggleEditPermission(domainKey, functionKey, fLabel, /*silent*/ true);
            if (!hasView) await togglePermission(domainKey, functionKey, fLabel);
        } else if (targetLevel === 'edit') {
            if (!hasEdit) await toggleEditPermission(domainKey, functionKey, fLabel);
        }
    }

    async function toggleEditPermission(domainKey, functionKey, fLabel, silent) {
        const selUserNow = users.find(u => u.id === selectedUserId);
        if (!selUserNow) return;

        setUsers(prev => prev.map(u => {
            if (u.id !== selectedUserId) return u;
            const editPerms = { ...(u.edit_permissions || {}) };
            const current = editPerms[domainKey] || [];
            const isOn = current.includes(functionKey);
            editPerms[domainKey] = isOn ? current.filter(k => k !== functionKey) : [...current, functionKey];
            // Granting edit auto-grants view, mirrored client-side too.
            let funcPerms = u.function_permissions;
            if (!isOn) {
                const currentView = funcPerms?.[domainKey] || [];
                if (!currentView.includes(functionKey)) {
                    funcPerms = { ...(funcPerms || {}), [domainKey]: [...currentView, functionKey] };
                }
            }
            if (!silent) showToast(`${isOn ? 'Edit removed' : 'Edit granted'} — ${fLabel}`, !isOn);
            return { ...u, edit_permissions: editPerms, function_permissions: funcPerms };
        }));

        try {
            await fetch(`${API_BASE}/auth?action=toggle_edit_permission`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ id: selectedUserId, domain: domainKey, function_key: functionKey }),
            });
        } catch (e) {
            console.error('Edit toggle failed, reloading to resync', e);
            loadUsers();
        }
    }

    async function addUser() {
        if (!addForm.username || !addForm.password) {
            setMsg({ type: 'error', text: 'Username and password are required' });
            return;
        }
        setSaving(true); setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/auth?action=add_user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                    ...addForm,
                    function_permissions: DEFAULT_PERMS_BY_ROLE[addForm.role] || {},
                }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type: 'success', text: `User "${addForm.username}" created` });
                setShowAdd(false);
                setAddForm({ username: '', password: '', role: 'kam', display_name: '' });
                loadUsers();
            } else {
                setMsg({ type: 'error', text: data.error || 'Failed to create user' });
            }
        } catch (e) { setMsg({ type: 'error', text: 'Network error' }); }
        finally { setSaving(false); }
    }

    async function toggleActive(u) {
        try {
            await fetch(`${API_BASE}/auth?action=update_user`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ id: u.id, active: u.active ? 0 : 1 }),
            });
            loadUsers();
        } catch (e) { console.error(e); }
    }

    function openEdit(u) {
        setEditId(u.id);
        setEditForm({ id: u.id, display_name: u.display_name || '', role: u.role, password: '' });
        setMsg(null);
    }

    async function saveEdit() {
        setSaving(true); setMsg(null);
        try {
            const payload = { ...editForm };
            if (!payload.password) delete payload.password;
            const res = await fetch(`${API_BASE}/auth?action=update_user`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type: 'success', text: 'Saved successfully' });
                setEditId(null);
                loadUsers();
            } else {
                setMsg({ type: 'error', text: data.error || 'Failed to save' });
            }
        } catch (e) { setMsg({ type: 'error', text: 'Network error' }); }
        finally { setSaving(false); }
    }

    const selUser = users.find(u => u.id === selectedUserId);
    const { granted, total } = selUser ? countGranted(selUser.function_permissions) : { granted: 0, total: 0 };
    const pct = total > 0 ? Math.round((granted / total) * 100) : 0;

    const filteredUsers = users.filter(u => {
        if (activeFilter !== 'All roles' && u.role !== activeFilter) return false;
        if (searchQuery && !`${u.display_name} ${u.username}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });

    return (
        <div style={S.page}>
            <style>{CSS}</style>

            {/* SIDEBAR */}
            <aside style={S.sidebar}>
                <div style={S.sidebarHead}>
                    <img src="/soulflower-logo.png" alt="Soulflower" style={{ height: 38, width: 'auto', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <nav style={S.sidebarNav}>
                    {[
                        { id: 'users', label: 'Users', IconEl: IconUsers, badge: String(users.length), badgeBg: '#EEE9F8', badgeColor: '#6E58A6' },
                    ].map(n => {
                        const active = section === n.id;
                        return (
                            <div key={n.id} onClick={() => goSection(n.id)} className="nav-item"
                                style={{
                                    ...S.navItem,
                                    background: active ? '#EEE9F8' : 'transparent',
                                    color: active ? '#4C3B78' : '#6B6379',
                                    fontWeight: active ? 600 : 400,
                                    transform: active ? 'translateX(2px)' : 'translateX(0)',
                                }}>
                                <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? '#6E58A6' : '#9A90AE' }}>
                                    <n.IconEl size={20} />
                                </span>
                                <span style={{ flex: 1 }}>{n.label}</span>
                                {n.badge && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: n.badgeBg, color: n.badgeColor }}>{n.badge}</span>}
                            </div>
                        );
                    })}

                    <div style={S.sidebarLabel}>ADMIN CONSOLE</div>

                    {[
                        { id: 'roles', label: 'Roles & Permissions', IconEl: IconShield },
                        { id: 'notifications', label: 'Notification Recipients', IconEl: IconMail },
                        { id: 'whatsapp', label: 'WhatsApp Whitelist', IconEl: IconMessage },
                        { id: 'usage', label: 'Portal Usage', IconEl: IconActivity },
                    ].map(n => {
                        const active = section === n.id;
                        return (
                            <div key={n.id} onClick={() => goSection(n.id)} className="nav-item"
                                style={{
                                    ...S.navItem,
                                    background: active ? '#EEE9F8' : 'transparent',
                                    color: active ? '#4C3B78' : '#6B6379',
                                    fontWeight: active ? 600 : 400,
                                    transform: active ? 'translateX(2px)' : 'translateX(0)',
                                }}>
                                <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? '#6E58A6' : '#9A90AE' }}>
                                    <n.IconEl size={20} />
                                </span>
                                <span style={{ flex: 1 }}>{n.label}</span>
                            </div>
                        );
                    })}
                </nav>
            </aside>

            {/* MAIN */}
            <div style={S.main}>
                {/* TOPBAR */}
                <div style={S.topbar}>
                    <div style={S.searchBox}>
                        <IconSearch size={17} style={{ color: '#9A90AE' }} />
                        <input
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search users by name or username…"
                            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, color: '#231C30', fontFamily: 'inherit', flex: 1 }}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                        <button onClick={() => navigate('/landing')} className="btn-home" style={S.homeBtn}>
                            <IconHome size={18} /> Home Page
                        </button>
                        <button onClick={() => setConfirmSignOut(true)} className="btn-signout" style={S.signOutBtn}>
                            Sign Out
                        </button>
                        <span style={{ width: 1, height: 34, background: '#ECE7F2' }} />
                        {/* User icon — display only, not a button, per instruction */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={S.avatar}><IconUser size={22} strokeWidth={1.7} /></div>
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontWeight: 600, fontSize: 16, color: '#231C30' }}>{user?.display_name || user?.username}</div>
                                <div style={{ fontSize: 12, color: '#8C82A0', fontWeight: 400 }}>{formatRole(user?.role)}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CONTENT */}
                <div style={S.content}>
                    {loading ? (
                        <div style={{ textAlign: 'center', color: '#9A90AE', padding: '80px 0', fontSize: 15 }}>Loading...</div>
                    ) : (
                        <>
                            {section === 'users' && (
                                <div key={animKey} className="fade-slide">
                                    <div style={S.headerRow}>
                                        <div>
                                            <h1 style={S.h1}>User <span style={{ fontWeight: 600, color: '#6E58A6' }}>Management</span></h1>
                                            <p style={S.sub}>Manage portal access and permissions for your team.</p>
                                        </div>
                                        <button className="btn-primary" style={S.primaryBtn} onClick={() => { setShowAdd(s => !s); setMsg(null); }}>
                                            {showAdd ? <IconX size={18} /> : <IconPlus size={18} />}
                                            {showAdd ? 'Cancel' : 'Add User'}
                                        </button>
                                    </div>

                                    {msg && (
                                        <div style={{ padding: '12px 18px', borderRadius: 9, fontSize: 14, fontWeight: 600, marginBottom: 20,
                                            background: msg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                                            color: msg.type === 'success' ? '#166534' : '#991B1B',
                                            border: `1px solid ${msg.type === 'success' ? '#BBF7D0' : '#FECACA'}` }}>
                                            {msg.text}
                                        </div>
                                    )}

                                    {showAdd && (
                                        <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginBottom: 24, border: '1.5px solid #E9D5FF', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ display: 'inline-block', width: 4, height: 18, background: '#8B5CF6', borderRadius: 2 }} />
                                                New User
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 20 }}>
                                                {[
                                                    ['Username', 'username', 'text', 'e.g. john'],
                                                    ['Display Name', 'display_name', 'text', 'e.g. John Doe'],
                                                    ['Password', 'password', 'password', 'Min 6 chars'],
                                                ].map(([label, key, type, ph]) => (
                                                    <div key={key}>
                                                        <label style={lbl}>{label}</label>
                                                        <input type={type} placeholder={ph} style={inp}
                                                            value={addForm[key]}
                                                            onChange={e => setAddForm(f => ({ ...f, [key]: e.target.value }))} />
                                                    </div>
                                                ))}
                                                <div>
                                                    <label style={lbl}>Role</label>
                                                    <select style={inp} value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}>
                                                        {ROLES.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 12.5, color: '#8C82A0', marginBottom: 16 }}>
                                                Default permissions for this role will be pre-filled — adjust per-user under Roles &amp; Permissions after creating.
                                            </div>
                                            <div style={{ display: 'flex', gap: 10 }}>
                                                <button onClick={addUser} disabled={saving} style={{ padding: '10px 24px', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                                                    {saving ? 'Creating...' : 'Create User'}
                                                </button>
                                                <button onClick={() => { setShowAdd(false); setMsg(null); }} style={{ padding: '10px 18px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22, flexWrap: 'wrap' }}>
                                        {FILTERS.map(f => {
                                            const active = f === activeFilter;
                                            return (
                                                <span key={f} onClick={() => setActiveFilter(f)}
                                                    style={{ padding: '10px 18px', borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                                                        background: active ? '#6E58A6' : '#fff', color: active ? '#fff' : '#6B6379', border: `1px solid ${active ? '#6E58A6' : '#E4DEEE'}` }}>
                                                    {f === 'All roles' ? f : formatRole(f)}
                                                </span>
                                            );
                                        })}
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                        {filteredUsers.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#9A90AE', padding: '60px 0' }}>No users match this filter.</div>
                                        ) : filteredUsers.map(u => {
                                            const rs = ROLE_STYLE[u.role] || { tint: '#F1EEF6', color: '#8C82A0' };
                                            const isEditing = editId === u.id;
                                            return (
                                                <div key={u.id} style={{ borderRadius: 16, background: '#fff', border: isEditing ? '1.5px solid #C4B5FD' : '1px solid #ECE7F2', overflow: 'hidden' }}>
                                                    <div className="user-row" style={S.userRow}>
                                                        <div style={{ ...S.userAvatar, background: rs.color }}>{initialOf(u.display_name || u.username)}</div>
                                                        <div style={{ flex: 'none', width: 190 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <span style={{ fontSize: 18, fontWeight: 600, color: '#231C30' }}>{u.display_name || u.username}</span>
                                                                <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: rs.tint, color: rs.color }}>{formatRole(u.role)}</span>
                                                            </div>
                                                            <div style={{ fontSize: 13, color: '#8C82A0', fontWeight: 300, marginTop: 3 }}>@{u.username}</div>
                                                        </div>
                                                        <div style={{ flex: 1 }} />
                                                        <div style={{ flex: 'none', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: u.active ? '#5C7E60' : '#C25E77' }}>
                                                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.active ? '#5C7E60' : '#C25E77' }} />
                                                                {u.active ? 'Active' : 'Inactive'}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: '#A79EB6', fontWeight: 300 }}>Updated {u.updated_at || u.created_at || '—'}</span>
                                                        </div>
                                                        <div style={{ flex: 'none', display: 'flex', gap: 10 }}>
                                                            <button className="btn-edit" style={S.editBtn} onClick={() => isEditing ? setEditId(null) : openEdit(u)}>
                                                                {isEditing ? 'Close' : 'Edit'}
                                                            </button>
                                                            <button className="btn-deactivate" style={S.deactivateBtn} onClick={() => toggleActive(u)}>
                                                                {u.active ? 'Deactivate' : 'Activate'}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {isEditing && (
                                                        <div style={{ padding: '20px 24px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 18 }}>
                                                                <div>
                                                                    <label style={lbl}>Display Name</label>
                                                                    <input type="text" style={inp} value={editForm.display_name}
                                                                        onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))} />
                                                                </div>
                                                                <div>
                                                                    <label style={lbl}>Role</label>
                                                                    <select style={inp} value={editForm.role}
                                                                        onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                                                                        {ROLES.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label style={lbl}>New Password</label>
                                                                    <input type="password" placeholder="Leave blank to keep" style={inp}
                                                                        value={editForm.password}
                                                                        onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} />
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                                <button onClick={saveEdit} disabled={saving} style={{ padding: '10px 28px', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                                                                    {saving ? 'Saving...' : 'Save Changes'}
                                                                </button>
                                                                <button onClick={() => { setEditId(null); setMsg(null); }} style={{ padding: '10px 18px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                                                    Cancel
                                                                </button>
                                                                {msg && <span style={{ fontSize: 13, fontWeight: 600, color: msg.type === 'success' ? '#166534' : '#991B1B' }}>{msg.text}</span>}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {section === 'roles' && (
                                <div key={animKey} className="fade-slide">
                                    <div style={{ marginBottom: 28 }}>
                                        <h1 style={S.h1}>User <span style={{ fontWeight: 600, color: '#6E58A6' }}>Permissions</span></h1>
                                        <p style={S.sub}>Access is set per user. Pick someone, then flip on the exact domains and functions they need.</p>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24, alignItems: 'start' }}>
                                        {/* USER PICKER */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {users.map(u => {
                                                const active = u.id === selectedUserId;
                                                const rs = ROLE_STYLE[u.role] || { tint: '#F1EEF6', color: '#8C82A0' };
                                                return (
                                                    <div key={u.id} onClick={() => setSelectedUserId(u.id)} className="picker-row"
                                                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                                                            background: active ? '#EEE9F8' : '#fff', border: `1px solid ${active ? '#C9B8EC' : '#ECE7F2'}`,
                                                            transform: active ? 'translateX(3px)' : 'translateX(0)' }}>
                                                        <div style={{ flex: 'none', width: 42, height: 42, borderRadius: 12, background: rs.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 16 }}>
                                                            {initialOf(u.display_name || u.username)}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 14.5, fontWeight: 600, color: '#231C30' }}>{u.display_name || u.username}</div>
                                                            <div style={{ fontSize: 12, color: '#8C82A0', fontWeight: 300 }}>{formatRole(u.role)}</div>
                                                        </div>
                                                        {active && <span className="pop-in" style={{ width: 8, height: 8, borderRadius: '50%', background: '#6E58A6', flex: 'none' }} />}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* PERMISSION PANEL */}
                                        {selUser && (
                                            <div>
                                                <div key={selUser.id} className="fade-slide" style={S.summaryCard}>
                                                    <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 19 }}>
                                                        {initialOf(selUser.display_name || selUser.username)}
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: 20, fontWeight: 500 }}>{selUser.display_name || selUser.username}</div>
                                                        <div style={{ fontSize: 13, color: '#E0D6F2', fontWeight: 300, marginTop: 2 }}>@{selUser.username} · {formatRole(selUser.role)} role</div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        {selUser.role === 'admin' ? (
                                                            <div style={{ fontSize: 13, color: '#D8CCEE', fontWeight: 500 }}>Unrestricted access</div>
                                                        ) : (
                                                            <>
                                                                <div style={{ fontSize: 26, fontWeight: 600 }}>{granted}<span style={{ fontSize: 15, fontWeight: 400, color: '#D8CCEE' }}>/{total}</span></div>
                                                                <div style={{ fontSize: 11.5, color: '#D8CCEE', fontWeight: 300, letterSpacing: '0.04em' }}>FUNCTIONS GRANTED</div>
                                                                <div style={{ marginTop: 8, height: 6, width: 150, borderRadius: 999, background: 'rgba(255,255,255,0.22)', overflow: 'hidden' }}>
                                                                    <div style={{ height: '100%', width: `${pct}%`, background: '#fff', borderRadius: 999, transition: 'width .35s ease' }} />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {selUser.role === 'admin' ? (
                                                    <div style={{ padding: 40, textAlign: 'center', color: '#9A90AE', background: '#fff', borderRadius: 16, border: '1px solid #ECE7F2' }}>
                                                        Admin accounts have unrestricted access to every domain and function — nothing to toggle here.
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                        {DOMAINS.map(d => (
                                                            <div key={d.key} className="perm-card" style={S.permCard}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                                                    <div style={{ width: 38, height: 38, borderRadius: 10, background: d.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', color: d.color, fontWeight: 700, fontSize: 15 }}>
                                                                        {d.label[0]}
                                                                    </div>
                                                                    <span style={{ fontSize: 17, fontWeight: 500, color: '#231C30' }}>{d.label}</span>
                                                                </div>
                                                                {d.functions.map(f => {
                                                                    const hasView = (selUser.function_permissions?.[d.key] || []).includes(f.key);
                                                                    const hasEdit = (selUser.edit_permissions?.[d.key] || []).includes(f.key);
                                                                    const level = hasEdit ? 'edit' : hasView ? 'view' : 'none';
                                                                    const LEVELS = [
                                                                        { id: 'none', label: 'No Access' },
                                                                        { id: 'view', label: 'View Only' },
                                                                        { id: 'edit', label: 'Edit' },
                                                                    ];
                                                                    return (
                                                                        <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #F4F1F8' }}>
                                                                            <span style={{ fontSize: 13.5, color: '#4A4356', fontWeight: 400 }}>{f.label}</span>
                                                                            <div style={{ display: 'flex', gap: 3, background: '#F4F1F8', borderRadius: 999, padding: 3 }}>
                                                                                {LEVELS.map(lv => (
                                                                                    <div key={lv.id} onClick={() => setPermissionLevel(d.key, f.key, f.label, lv.id)}
                                                                                        className="level-pill"
                                                                                        title={lv.label}
                                                                                        style={{
                                                                                            padding: '4px 10px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                                                                                            whiteSpace: 'nowrap', transition: 'background .18s ease, color .18s ease',
                                                                                            background: level === lv.id ? (lv.id === 'edit' ? d.color : lv.id === 'view' ? '#8C82A0' : '#fff') : 'transparent',
                                                                                            color: level === lv.id ? (lv.id === 'none' ? '#C25E77' : '#fff') : '#8C82A0',
                                                                                            boxShadow: level === lv.id && lv.id === 'none' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                                                                                        }}>
                                                                                        {lv.id === 'none' ? 'None' : lv.id === 'view' ? 'View' : 'Edit'}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {section === 'notifications' && (
                                <div key={animKey} className="fade-slide">
                                    <div style={{ marginBottom: 28 }}>
                                        <h1 style={S.h1}>Notification <span style={{ fontWeight: 600, color: '#6E58A6' }}>Recipients</span></h1>
                                        <p style={S.sub}>Who gets emailed for each alert, per workspace. Event alerts (GRN Discrepancy, PO's Received, SO/Invoice Alerts, ...) send instantly; report alerts (Daily Summary Report, ...) run on the cadence set below.</p>
                                    </div>

                                    {notifLoading ? (
                                        <div style={{ textAlign: 'center', color: '#9A90AE', padding: '60px 0', fontSize: 15 }}>Loading...</div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 18, alignItems: 'start' }}>
                                            {NOTIFICATION_DOMAINS.map(d => (
                                                <div key={d.key} style={{ background: '#fff', borderRadius: 16, border: '1px solid #ECE7F2', padding: 20 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flex: 'none' }} />
                                                        <span style={{ fontSize: 15, fontWeight: 600, color: '#231C30' }}>{d.label}</span>
                                                    </div>

                                                    {d.features.map((f, fi) => {
                                                        const mapKey = featureMapKey(d.key, f.key);
                                                        const emails = notifRecipients.filter(r => r.domain_key === d.key && (r.feature_key || null) === f.key);
                                                        const schedule = notifSchedules.find(s => s.domain_key === d.key && s.feature_key === f.key);
                                                        const showFeatureLabel = f.key !== null; // "General" (null-key) domains skip the redundant sub-label

                                                        return (
                                                            <div key={f.key ?? 'general'} style={{ marginBottom: fi < d.features.length - 1 ? 18 : 0, paddingBottom: fi < d.features.length - 1 ? 18 : 0, borderBottom: fi < d.features.length - 1 ? '1px solid #F4F1F8' : 'none' }}>
                                                                {showFeatureLabel && (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#4A4356' }}>{f.label}</span>
                                                                        {f.comingSoon && (
                                                                            <span style={{ fontSize: 9.5, fontWeight: 700, color: '#9A7B3E', background: '#F5EDDC', padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.3 }}>Coming soon</span>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {f.kind === 'report' && (
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                                                        <select
                                                                            value={schedule?.frequency || 'daily'}
                                                                            onChange={e => saveNotifSchedule(d.key, f.key, { frequency: e.target.value })}
                                                                            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ECE7F2', fontSize: 11.5, color: '#4A4356' }}
                                                                        >
                                                                            {FREQUENCIES.map(fr => <option key={fr.key} value={fr.key}>{fr.label}</option>)}
                                                                        </select>
                                                                        <input
                                                                            type="time"
                                                                            value={schedule?.time_ist || '18:30'}
                                                                            onChange={e => saveNotifSchedule(d.key, f.key, { time_ist: e.target.value })}
                                                                            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ECE7F2', fontSize: 11.5, color: '#4A4356' }}
                                                                        />
                                                                        <span style={{ fontSize: 10.5, color: '#B2AAC4', alignSelf: 'center' }}>IST</span>
                                                                        {(schedule?.frequency || 'daily') === 'weekly' && (
                                                                            <select
                                                                                value={schedule?.day_of_week ?? 0}
                                                                                onChange={e => saveNotifSchedule(d.key, f.key, { day_of_week: Number(e.target.value) })}
                                                                                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ECE7F2', fontSize: 11.5, color: '#4A4356' }}
                                                                            >
                                                                                {DAYS_OF_WEEK.map(dw => <option key={dw.value} value={dw.value}>{dw.label}</option>)}
                                                                            </select>
                                                                        )}
                                                                        {(schedule?.frequency || 'daily') === 'monthly' && (
                                                                            <input
                                                                                type="number" min={1} max={31}
                                                                                value={schedule?.day_of_month ?? 1}
                                                                                onChange={e => saveNotifSchedule(d.key, f.key, { day_of_month: Number(e.target.value) })}
                                                                                title="Day of month"
                                                                                style={{ width: 52, padding: '6px 8px', borderRadius: 6, border: '1px solid #ECE7F2', fontSize: 11.5, color: '#4A4356' }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {f.perKam && (
                                                                    <div style={{ fontSize: 11, color: '#9A90AE', fontStyle: 'italic', margin: '-2px 0 8px' }}>
                                                                        "All SOs" emails get every alert (and the instant "no KAM assigned" ones); a KAM-tagged email gets only that KAM's parties, after 5 min.
                                                                    </div>
                                                                )}
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                                                                    {emails.length === 0 && (
                                                                        <div style={{ fontSize: 12.5, color: '#B2AAC4' }}>No recipients yet</div>
                                                                    )}
                                                                    {emails.map(r => (
                                                                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10, background: d.tint }}>
                                                                            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#231C30', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</span>
                                                                            {f.perKam && (
                                                                                <select
                                                                                    value={r.kam_name || ''}
                                                                                    onChange={e => setRecipientKam(r.id, e.target.value)}
                                                                                    title="All SOs, or only one KAM's parties"
                                                                                    style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #DDD4EA', fontSize: 11.5, color: r.kam_name ? '#6E58A6' : '#4A4356', fontWeight: r.kam_name ? 700 : 500, background: '#fff', maxWidth: 130 }}
                                                                                >
                                                                                    <option value="">All SOs</option>
                                                                                    {kamNames.map(k => <option key={k} value={k}>KAM: {k}</option>)}
                                                                                </select>
                                                                            )}
                                                                            <span onClick={() => removeNotifRecipient(r.id, r.email)} style={{ cursor: 'pointer', color: '#9A90AE', fontSize: 13 }} title="Remove">✕</span>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                <div style={{ display: 'flex', gap: 8 }}>
                                                                    <input
                                                                        type="email"
                                                                        placeholder="Add email…"
                                                                        value={newRecipientEmail[mapKey] || ''}
                                                                        onChange={e => setNewRecipientEmail(prev => ({ ...prev, [mapKey]: e.target.value }))}
                                                                        onKeyDown={e => { if (e.key === 'Enter') addNotifRecipient(d.key, f.key); }}
                                                                        style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12.5, outline: 'none' }}
                                                                    />
                                                                    {f.perKam && (
                                                                        <select
                                                                            value={newRecipientKam[mapKey] || ''}
                                                                            onChange={e => setNewRecipientKam(prev => ({ ...prev, [mapKey]: e.target.value }))}
                                                                            title="All SOs, or only one KAM's parties"
                                                                            style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12, color: '#4A4356', maxWidth: 120 }}
                                                                        >
                                                                            <option value="">All SOs</option>
                                                                            {kamNames.map(k => <option key={k} value={k}>KAM: {k}</option>)}
                                                                        </select>
                                                                    )}
                                                                    <button onClick={() => addNotifRecipient(d.key, f.key)} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#6E58A6', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                                                                        Add
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {section === 'whatsapp' && (
                                <div key={animKey} className="fade-slide">
                                    <div style={{ marginBottom: 28 }}>
                                        <h1 style={S.h1}>WhatsApp <span style={{ fontWeight: 600, color: '#6E58A6' }}>Whitelist</span></h1>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, marginBottom: 20, maxWidth: 640 }}>
                                        <input placeholder="Phone number" value={waAddForm.phone}
                                            onChange={e => setWaAddForm(f => ({ ...f, phone: e.target.value }))}
                                            style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12.5, outline: 'none' }} />
                                        <input placeholder="Name" value={waAddForm.name}
                                            onChange={e => setWaAddForm(f => ({ ...f, name: e.target.value }))}
                                            style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12.5, outline: 'none' }} />
                                        <input placeholder="Nickname" value={waAddForm.nickname}
                                            onChange={e => setWaAddForm(f => ({ ...f, nickname: e.target.value }))}
                                            style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12.5, outline: 'none' }} />
                                        <input placeholder="Role" value={waAddForm.role}
                                            onChange={e => setWaAddForm(f => ({ ...f, role: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') addWaNumber(); }}
                                            style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #ECE7F2', fontSize: 12.5, outline: 'none' }} />
                                        <button onClick={addWaNumber} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#6E58A6', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Add</button>
                                    </div>

                                    {waLoading ? (
                                        <div style={{ textAlign: 'center', color: '#9A90AE', padding: '60px 0', fontSize: 15 }}>Loading...</div>
                                    ) : waWhitelist.length === 0 ? (
                                        <div style={{ fontSize: 12.5, color: '#B2AAC4' }}>No numbers whitelisted yet</div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640 }}>
                                            {waWhitelist.map(r => (
                                                <div key={r.phone} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#fff', border: '1px solid #ECE7F2' }}>
                                                    <span style={{ fontSize: 12.5, color: '#231C30', fontWeight: 600, width: 110, flex: 'none' }}>{r.phone}</span>
                                                    {waEditPhone === r.phone ? (
                                                        <>
                                                            <input placeholder="Name" value={waEditForm.name} onChange={e => setWaEditForm(f => ({ ...f, name: e.target.value }))}
                                                                style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6, border: '1px solid #DDD4EA', fontSize: 12 }} />
                                                            <input placeholder="Nickname" value={waEditForm.nickname} onChange={e => setWaEditForm(f => ({ ...f, nickname: e.target.value }))}
                                                                style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6, border: '1px solid #DDD4EA', fontSize: 12 }} />
                                                            <input placeholder="Role" value={waEditForm.role} onChange={e => setWaEditForm(f => ({ ...f, role: e.target.value }))}
                                                                style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6, border: '1px solid #DDD4EA', fontSize: 12 }} />
                                                            <span onClick={async () => { const ok = await saveWaNumber({ phone: r.phone, name: waEditForm.name.trim() || null, nickname: waEditForm.nickname.trim() || null, role: waEditForm.role.trim() || null }, 'Saved'); if (ok) setWaEditPhone(null); }}
                                                                style={{ cursor: 'pointer', color: '#6E58A6', fontSize: 13 }} title="Save">✓</span>
                                                            <span onClick={() => setWaEditPhone(null)} style={{ cursor: 'pointer', color: '#9A90AE', fontSize: 13 }} title="Cancel">✕</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span style={{ flex: 1, fontSize: 12.5, color: '#4A4356' }}>{r.name || '—'}</span>
                                                            <span style={{ flex: 1, fontSize: 12.5, color: r.nickname ? '#6E58A6' : '#B2AAC4', fontWeight: r.nickname ? 600 : 400 }}>{r.nickname || 'no nickname'}</span>
                                                            <span style={{ flex: 1, fontSize: 12.5, color: '#4A4356' }}>{r.role || '—'}</span>
                                                            <span onClick={() => { setWaEditPhone(r.phone); setWaEditForm({ name: r.name || '', nickname: r.nickname || '', role: r.role || '' }); }}
                                                                style={{ cursor: 'pointer', color: '#9A90AE', fontSize: 12 }} title="Edit">Edit</span>
                                                            <span onClick={() => removeWaNumber(r.phone)} style={{ cursor: 'pointer', color: '#9A90AE', fontSize: 13 }} title="Remove">✕</span>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {section === 'usage' && (
                                <div key={animKey} className="fade-slide">
                                    <div style={{ ...S.headerRow, marginBottom: 20 }}>
                                        <div>
                                            <h1 style={S.h1}>Portal <span style={{ fontWeight: 600, color: '#6E58A6' }}>Usage</span></h1>
                                            <p style={S.sub}>Who's using the portal, and which dashboards they actually open — based on real activity, not login/logout.</p>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            {USAGE_RANGES.map(r => (
                                                <span key={r.key} onClick={() => { setUsageRange(r.key); loadUsageData(r.key); }}
                                                    style={{ padding: '9px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                                                        background: usageRange === r.key ? '#6E58A6' : '#fff', color: usageRange === r.key ? '#fff' : '#6B6379', border: `1px solid ${usageRange === r.key ? '#6E58A6' : '#E4DEEE'}` }}>
                                                    {r.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {usageLoading ? (
                                        <div style={{ textAlign: 'center', color: '#9A90AE', padding: '60px 0', fontSize: 15 }}>Loading...</div>
                                    ) : (
                                        <>
                                            {/* Daily active users chart */}
                                            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #ECE7F2', padding: 20, marginBottom: 20, height: 260 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, color: '#231C30', marginBottom: 10 }}>Daily Active Users</div>
                                                {dailyActive.length === 0 ? (
                                                    <div style={{ textAlign: 'center', color: '#9A90AE', padding: '60px 0', fontSize: 13.5 }}>No activity recorded in this range yet.</div>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height="88%">
                                                        <BarChart data={dailyActive} margin={{ top: 4, right: 8, left: -14, bottom: 4 }}>
                                                            <CartesianGrid stroke="#F4F1F8" vertical={false} />
                                                            <XAxis dataKey="day" tick={{ fontFamily: 'Poppins', fontSize: 11, fill: '#8C82A0' }} stroke="#ECE7F2" />
                                                            <YAxis allowDecimals={false} tick={{ fontFamily: 'Poppins', fontSize: 11, fill: '#8C82A0' }} stroke="#ECE7F2" />
                                                            <Tooltip contentStyle={{ fontFamily: 'Poppins', fontSize: 12.5, borderRadius: 8, border: '1px solid #ECE7F2' }} cursor={{ fill: '#F4F1F8' }} />
                                                            <Bar dataKey="active_users" name="Active Users" fill="#6E58A6" radius={[4, 4, 0, 0]} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>

                                            {/* Role summary */}
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
                                                {roleSummary.map(r => (
                                                    <div key={r.role} style={{ background: '#fff', borderRadius: 14, border: '1px solid #ECE7F2', padding: '16px 18px' }}>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#8C82A0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{formatRole(r.role)}</div>
                                                        <div style={{ fontSize: 24, fontWeight: 600, color: '#231C30', marginTop: 4 }}>
                                                            {r.active_users}<span style={{ fontSize: 14, fontWeight: 400, color: '#B2AAC4' }}>/{r.total_users}</span>
                                                        </div>
                                                        <div style={{ fontSize: 11.5, color: '#A79EB6' }}>active of total</div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Team activity table */}
                                            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #ECE7F2', padding: 20, marginBottom: 20 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#231C30' }}>Team Activity <span style={{ fontWeight: 400, color: '#9A90AE' }}>(least active first)</span></div>
                                                    <button onClick={exportTeamActivity} style={{ padding: '8px 16px', border: '1px solid #E4DEEE', borderRadius: 8, background: '#fff', color: '#6E58A6', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                                        Export Excel
                                                    </button>
                                                </div>
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ textAlign: 'left', color: '#8C82A0', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                                <th style={{ padding: '8px 10px' }}>Name</th>
                                                                <th style={{ padding: '8px 10px' }}>Role</th>
                                                                <th style={{ padding: '8px 10px' }}>Last Active (IST)</th>
                                                                <th style={{ padding: '8px 10px' }}>Days Active</th>
                                                                <th style={{ padding: '8px 10px' }}>Total Visits</th>
                                                                <th style={{ padding: '8px 10px' }}>Avg Visit (min)</th>
                                                                <th style={{ padding: '8px 10px' }}>Most-Used Dashboard</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {teamActivity.map(u => {
                                                                const inactive = daysSince(u.last_active) >= 7;
                                                                return (
                                                                    <tr key={u.id} style={{ borderTop: '1px solid #F4F1F8', color: inactive ? '#C25E77' : '#231C30', fontWeight: inactive ? 600 : 400 }}>
                                                                        <td style={{ padding: '10px' }}>{u.display_name}</td>
                                                                        <td style={{ padding: '10px' }}>{formatRole(u.role)}</td>
                                                                        <td style={{ padding: '10px' }}>{fmtLastActive(u.last_active)}</td>
                                                                        <td style={{ padding: '10px' }}>{u.days_active}</td>
                                                                        <td style={{ padding: '10px' }}>{u.total_visits}</td>
                                                                        <td style={{ padding: '10px' }}>{u.avg_visit_minutes ?? '—'}</td>
                                                                        <td style={{ padding: '10px' }}>{u.most_used_page || '—'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* Dashboard popularity */}
                                            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #ECE7F2', padding: 20 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, color: '#231C30', marginBottom: 14 }}>Dashboard Popularity</div>
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ textAlign: 'left', color: '#8C82A0', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                                <th style={{ padding: '8px 10px' }}>Dashboard</th>
                                                                <th style={{ padding: '8px 10px' }}>Views</th>
                                                                <th style={{ padding: '8px 10px' }}>Unique Users</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {dashboardPopularity.map(p => (
                                                                <tr key={p.page_name} style={{ borderTop: '1px solid #F4F1F8' }}>
                                                                    <td style={{ padding: '10px' }}>{p.page_name}</td>
                                                                    <td style={{ padding: '10px' }}>{p.views}</td>
                                                                    <td style={{ padding: '10px' }}>{p.unique_users}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {(() => {
                                                    const seen = new Set(dashboardPopularity.map(p => p.page_name));
                                                    const zeroView = ALL_DASHBOARD_LABELS.filter(l => !seen.has(l));
                                                    if (zeroView.length === 0) return null;
                                                    return (
                                                        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA' }}>
                                                            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>Not used in this period</div>
                                                            <div style={{ fontSize: 12.5, color: '#991B1B' }}>{zeroView.join(', ')}</div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* TOAST */}
            {toast && (
                <div className="toast-in" style={{ position: 'fixed', bottom: 32, right: 40, display: 'flex', alignItems: 'center', gap: 12, padding: '16px 22px', borderRadius: 14, background: '#231C30', color: '#fff', fontSize: 14, fontWeight: 500, boxShadow: '0 12px 32px rgba(35,28,48,0.28)', zIndex: 50 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: toast.on ? '#5C7E60' : '#C25E77', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                        {toast.on ? <IconCheck size={13} stroke="#fff" strokeWidth={3} /> : <IconX size={13} stroke="#fff" strokeWidth={3} />}
                    </span>
                    {toast.msg}
                </div>
            )}

            {/* SIGN OUT CONFIRM */}
            {confirmSignOut && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 360, boxShadow: '0 20px 50px rgba(35,28,48,0.3)' }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#231C30', marginBottom: 8 }}>Sign out?</div>
                        <div style={{ fontSize: 13.5, color: '#6B6379', marginBottom: 22 }}>You'll need to log in again to access Admin Centre.</div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setConfirmSignOut(false)} style={{ padding: '10px 18px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button onClick={onLogout} style={{ padding: '10px 20px', background: '#C25E77', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/* Styles                                                                  */
/* ---------------------------------------------------------------------- */
const CSS = `
@keyframes fadeSlideUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes toastIn { from { opacity:0; transform:translateY(12px) scale(.96); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes popIn { from { opacity:0; transform:scale(.85); } to { opacity:1; transform:scale(1); } }
.fade-slide { animation: fadeSlideUp .4s ease; }
.toast-in { animation: toastIn .25s ease; }
.pop-in { animation: popIn .25s ease; }
.nav-item { transition: background .2s ease, color .2s ease, transform .15s ease; }
.nav-item:hover { background:#F6F2FB !important; transform: translateX(2px) !important; }
.nav-item:active { transform: scale(0.97) !important; }
.btn-icon { transition: transform .15s ease, border-color .15s ease; }
.btn-icon:hover { transform: translateY(-2px); border-color:#C9B8EC; }
.btn-home { transition: background .18s ease, transform .15s ease; }
.btn-home:hover { background:#5D4A8C !important; transform: translateY(-2px); }
.btn-signout { transition: background .15s ease; }
.btn-signout:hover { background:#F6F2FB !important; }
.btn-primary:hover { background:#5D4A8C !important; }
.btn-primary:active { transform: scale(0.97); }
.btn-edit, .btn-deactivate { transition: background .15s ease, transform .12s ease; }
.btn-edit:hover { background:#F6F2FB !important; transform: translateY(-1px); }
.btn-deactivate:hover { background:#FBF1F3 !important; transform: translateY(-1px); }
.btn-edit:active, .btn-deactivate:active { transform: scale(0.95); }
.user-row { transition: border-color .18s ease; }
.picker-row { transition: background .18s ease, border-color .18s ease, transform .15s ease; }
.picker-row:hover { border-color:#C9B8EC !important; transform: translateX(3px); }
.perm-card { transition: box-shadow .2s ease, transform .2s ease; }
.perm-card:hover { box-shadow: 0 10px 28px rgba(110,88,166,0.12); transform: translateY(-2px); }
.level-pill:hover { background: #E4DEEE !important; }
.level-pill:active { transform: scale(0.94); }
`;

const S = {
    page: { display: 'flex', minHeight: '100vh', background: '#FAF8FC', color: '#231C30', fontFamily: "'Poppins', 'Segoe UI', sans-serif" },
    sidebar: { flex: 'none', width: 264, background: '#fff', borderRight: '1px solid #ECE7F2', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh' },
    sidebarHead: { padding: '26px 24px', borderBottom: '1px solid #ECE7F2' },
    sidebarLabel: { fontSize: 11, letterSpacing: '0.24em', fontWeight: 600, color: '#8C82A0', margin: '18px 14px 10px', textAlign: 'center' },
    sidebarNav: { flex: 1, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' },
    navItem: { display: 'flex', alignItems: 'center', gap: 13, padding: '12px 14px', borderRadius: 11, cursor: 'pointer', fontSize: 14.5 },
    main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
    topbar: { flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 40px', borderBottom: '1px solid #ECE7F2', background: '#fff', position: 'sticky', top: 0, zIndex: 10 },
    searchBox: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, maxWidth: 440, padding: '11px 16px', borderRadius: 11, background: '#FAF8FC', border: '1px solid #ECE7F2' },
    homeBtn: { display: 'flex', alignItems: 'center', gap: 9, padding: '13px 22px', borderRadius: 11, border: 'none', background: '#6E58A6', color: '#fff', fontFamily: 'inherit', fontWeight: 500, fontSize: 15, cursor: 'pointer' },
    signOutBtn: { padding: '13px 18px', borderRadius: 11, border: '1px solid #E4DEEE', background: '#fff', color: '#6B6379', fontFamily: 'inherit', fontWeight: 500, fontSize: 14, cursor: 'pointer' },
    avatar: { width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#8E74C9,#5D4A8C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
    content: { flex: 1, padding: 40, overflowY: 'auto' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    h1: { fontWeight: 300, fontSize: 40, margin: 0, color: '#231C30', letterSpacing: '-0.02em' },
    sub: { margin: '12px 0 0', fontSize: 15, color: '#6B6379', fontWeight: 300 },
    primaryBtn: { display: 'flex', alignItems: 'center', gap: 9, padding: '14px 24px', borderRadius: 13, border: 'none', background: '#6E58A6', color: '#fff', fontFamily: 'inherit', fontWeight: 500, fontSize: 15, cursor: 'pointer' },
    userRow: { display: 'flex', alignItems: 'center', gap: 22, padding: '22px 26px' },
    userAvatar: { flex: 'none', width: 54, height: 54, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 20 },
    editBtn: { padding: '10px 20px', borderRadius: 10, border: '1px solid #E4DEEE', background: '#fff', color: '#6E58A6', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' },
    deactivateBtn: { padding: '10px 20px', borderRadius: 10, border: '1px solid #F1D9DF', background: '#fff', color: '#C25E77', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' },
    summaryCard: { display: 'flex', alignItems: 'center', gap: 16, padding: '22px 26px', borderRadius: 16, background: 'linear-gradient(160deg,#6E58A6,#4C3B78)', color: '#fff', marginBottom: 20 },
    permCard: { borderRadius: 16, background: '#fff', border: '1px solid #ECE7F2', padding: 22 },
};

const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 };
const inp = { padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 14, color: '#1A1A1A', background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };